/**
 * `composeOpsLogProvenance` — pure function composer for Account v0 home
 * `<OpsLogView />` provenance chip rendering at PHG client surface per
 * [`account_v0_home_design.md`](../../../../../account_v0_home_design.md)
 * §2.4 + §2.5 audit chain View 1 patient-perspective composition.
 *
 * **Phase 3 substantive composition gate FIRED at Layer (6) Phase 1 IMPL ship**
 * (PR #549 `9117a2b` MERGED) per [PR #544 §10 phasing](https://github.com/SmartHealthNetwork/SHN2026/pull/544)
 * + sub-pattern (h) consumer-side forward-looking shape commitment risk
 * discipline (per `feedback_empirical_verification_before_canonical_state_claim.md`).
 * Cascade authorization end-to-end verified empirically:
 * (C.2) cascade COMPLETE 5/5 SHIPPED + ADR-033 v0.2 substantive ratification
 * cycle close FIRED + Sprint 9+ Layer chain Phase 1+ IMPL cohort all MERGED.
 *
 * # Composition flow
 *
 * 1. Caller fetches ops log entries: `getPatientOpsLog({ subjectPciId })`
 *    → `GetPatientOpsLogResult.entries`
 * 2. Caller fetches attestations batch: `getInclusionProofAttestationsByRecordIds(recordIds)`
 *    → `ReadonlyMap<string, InclusionProofAttestation>` per Layer (6)
 *    [`IInclusionProofAttestationClient`](../../api/projection-service-client.ts)
 *    PR #549 wire-contract canonical
 * 3. This composer batch-verifies attestations + categorizes entries:
 *    `verified` (with `verifiedKid` + `attestedAt` provenance) /
 *    `failed` (with `reason` + `detail` failure) /
 *    `no-attestation` (graceful-handling per `feedback_phg_recovery_ux_honesty.md`
 *    content-honesty discipline analog)
 * 4. Consumer renders categorized entries: provenance chip on verified;
 *    failure indicator on failed; no provenance chip on no-attestation
 *    (honest "no anchor" framing per Doctrine v1.3 §8 B.1 NO SHDT BACKDOOR
 *    provenance discipline)
 *
 * # Doctrinal anchors (per Account v0 home design memo §6 binding)
 *
 * 1. **NO SHT BACKDOOR** (Doctrine v1.3 §8 B.1 + non-negotiability marker A.4) —
 *    provenance chip renders ONLY on verified entries; "verified by substrate"
 *    framing only when `verifyInclusionProofAttestation` ECDSA P-256 + JCS +
 *    detached JWS verification fires successfully
 * 2. **Recovery UX honesty** (Doctrine v1.3 §8 B.3 ELEVATE per
 *    `feedback_phg_recovery_ux_honesty.md`) — `no-attestation` category preserves
 *    honest framing (no false-provenance claim; no "pending" conflation with
 *    explicit user-action `cancel` per ADR-027 §1 step 4 explicit-affirmative-action
 *    discipline analog at consumer-side rendering)
 * 3. **Pattern B no-parallel-rendering-surface** (`feedback_pattern_b_no_parallel_rendering_surface.md`) —
 *    composition is client-side pure function; substrate provides projection
 *    data via API; client-side composes + renders
 * 4. **Active-session-only crypto** (`feedback_phg_client_active_session_crypto.md`) —
 *    `TrustedAttestationKeys` map is session-resident (read from config or
 *    fetched at session start); composer operates on structural metadata + verifier
 *    invocation; no cleartext key material at composer surface
 *
 * # Composes with
 *
 * - [PR #517 `verifyAttestationBatch`](https://github.com/SmartHealthNetwork/SHN2026/pull/517) — batch wrapper composing per-attestation `verifyInclusionProofAttestation` (PR #404) primitive into Account-v0-ops-log-friendly batch shape
 * - [PR #549 `IInclusionProofAttestationClient` Phase 1 IMPL](https://github.com/SmartHealthNetwork/SHN2026/pull/549) — Layer (6) DEV3 client-side mirror at `phg-client-web/src/api/projection-service-client.ts` `getInclusionProofAttestationsByRecordIds` batch lookup
 * - [PR #516 `IPatientOpsLogReader` content via PR #536 squash](https://github.com/SmartHealthNetwork/SHN2026/pull/536) — Layer (6) DEV3 client-side mirror `getPatientOpsLog` per Account v0 home §2.4 wire-contract
 * - [PR #544 §6.2 Layer (6) consumer-side composer pattern](https://github.com/SmartHealthNetwork/SHN2026/pull/544) — composition canonical at PHG client surface
 * - [DEV5 PR #517 ergonomic feedback §2.1 path (a)](https://github.com/SmartHealthNetwork/SHN2026/pull/517#issuecomment-4414076208) — filter-at-consumer-side recommendation; preserved at this composer
 */
import { verifyAttestationBatch, } from '@shn/sdk/audit';
/**
 * Compose ops log entries with attestation batch verification into provenance-
 * categorized rendering shape. Pure function (no UI; no React); consumer
 * renders categorized entries with appropriate UI per `OpsLogProvenanceEntry.provenance`
 * discriminator.
 *
 * Filter-at-consumer-side semantics per [DEV5 PR #517 ergonomic feedback §2.1
 * path (a)](https://github.com/SmartHealthNetwork/SHN2026/pull/517#issuecomment-4414076208):
 * entries without attestation in `attestationsMap` filter at composer boundary
 * (NOT passed to `verifyAttestationBatch`); render as `no-attestation` per
 * honest framing.
 *
 * @param entries - Ops log entries from `getPatientOpsLog`
 * @param attestationsMap - Attestation lookup from `getInclusionProofAttestationsByRecordIds` batch
 * @param trustedAttestationKeys - Session-resident substrate operator key registry
 * @param batchVerifier - Optional verifier injection for test seam; defaults to `verifyAttestationBatch`
 */
export async function composeOpsLogProvenance(entries, attestationsMap, trustedAttestationKeys, batchVerifier = verifyAttestationBatch) {
    const verifiableEntries = entries
        .filter(e => attestationsMap.has(e.recordId))
        .map(e => ({ id: e.recordId, attestation: attestationsMap.get(e.recordId) }));
    // Minimal SDK consumer-contract alignment with Phase 4 §4.2 3-arm
    // AttestationBatchResult shape per DEV6 §4.2 IMPL + DEV5 substantive concur
    // 2026-05-11 cycle close. PHG composer adoption follow-on (i18n "no anchor yet"
    // + provenance rendering substantive content) substrate-state-event-paced
    // for DEV5 next-activation per Decoupled-then-integrate §2.10 discipline.
    const result = verifiableEntries.length > 0
        ? await batchVerifier(verifiableEntries, trustedAttestationKeys)
        : { verified: [], failed: [], noAttestation: [] };
    const verifiedById = new Map(result.verified.map(v => [v.id, v]));
    const failedById = new Map(result.failed.map(f => [f.id, f]));
    const composedEntries = entries.map(entry => {
        const verified = verifiedById.get(entry.recordId);
        if (verified !== undefined) {
            return {
                entry,
                provenance: 'verified',
                verifiedKid: verified.verifiedKid,
                attestedAt: verified.attestedAt,
            };
        }
        const failed = failedById.get(entry.recordId);
        if (failed !== undefined) {
            return {
                entry,
                provenance: 'failed',
                failureReason: failed.reason,
                failureDetail: failed.detail,
            };
        }
        return { entry, provenance: 'no-attestation' };
    });
    return { entries: composedEntries };
}
//# sourceMappingURL=compose-ops-log-provenance.js.map